import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { products, categories, siteImages } from '../data/mock';
import { useCart } from '../context/CartContext';
import { ShoppingCart, Filter, X, ChevronDown, PawPrint } from 'lucide-react';

const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('featured');
  const { addToCart } = useCart();

  const selectedCategory = searchParams.get('category') || '';

  useEffect(() => {
    document.title = 'Shop Pet Accessories & Equipment | Paw Supplies Group';
  }, []);

  const filteredProducts = useMemo(() => {
    let result = [...products];
    if (selectedCategory) {
      result = result.filter(p => p.categorySlug === selectedCategory);
    }
    switch (sortBy) {
      case 'price-low': result.sort((a, b) => a.price - b.price); break;
      case 'price-high': result.sort((a, b) => b.price - a.price); break;
      case 'name': result.sort((a, b) => a.name.localeCompare(b.name)); break;
      default: break;
    }
    return result;
  }, [selectedCategory, sortBy]);

  const handleCategoryChange = (categorySlug) => {
    if (categorySlug) setSearchParams({ category: categorySlug });
    else setSearchParams({});
  };

  const handleQuickAdd = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product, 1);
  };

  const currentCategory = categories.find(c => c.slug === selectedCategory);

  return (
    <div className="min-h-screen bg-brand-cream text-brand-brown">
      <Header />
      <CartDrawer />

      <main>
        {/* Page Banner */}
        <section className="relative h-56 md:h-72 overflow-hidden">
          <img
            src={siteImages.shopBanner}
            alt="Shop Pet Accessories"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-brown/85 via-brand-brown/60 to-brand-brown/30" />
          <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-brand-cream mb-2">
                <PawPrint className="w-5 h-5" />
                <span className="text-xs font-bold uppercase tracking-[0.25em]">Explore All</span>
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight" className-FONT-DISPLAY>
                {currentCategory ? currentCategory.name : 'Pet Accessories & Equipment'}
              </h1>
              <p className="mt-3 text-brand-cream max-w-2xl">
                {currentCategory
                  ? currentCategory.description
                  : 'Browse our complete collection of premium pet accessories curated for dogs, cats and small pets.'}
              </p>
            </div>
          </div>
        </section>

        {/* Breadcrumb */}
        <section className="bg-white border-b border-brand-divider">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <nav className="text-sm text-brand-brown-light">
              <Link to="/" className="hover:text-brand-orange-dark">Home</Link>
              <span className="mx-2">/</span>
              <span className="text-brand-brown">{currentCategory ? currentCategory.name : 'All Products'}</span>
            </nav>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar Filters */}
            <aside className={`lg:w-64 flex-shrink-0 ${showFilters ? 'block' : 'hidden lg:block'}`}>
              <div className="bg-white rounded-2xl p-6 ring-1 ring-brand-divider shadow-sm sticky top-32">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="font-bold text-brand-brown text-lg" className-FONT-DISPLAY>Categories</h2>
                  <button
                    onClick={() => setShowFilters(false)}
                    className="lg:hidden text-brand-brown-light"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="space-y-1">
                  <button
                    onClick={() => handleCategoryChange('')}
                    className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      !selectedCategory
                        ? 'bg-brand-cream text-brand-orange-dark'
                        : 'text-brand-brown hover:bg-brand-cream'
                    }`}
                  >
                    All Products
                    <span className="float-right text-brand-brown-light">{products.length}</span>
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleCategoryChange(cat.slug)}
                      className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        selectedCategory === cat.slug
                          ? 'bg-brand-cream text-brand-orange-dark'
                          : 'text-brand-brown hover:bg-brand-cream'
                      }`}
                    >
                      {cat.name}
                      <span className="float-right text-brand-brown-light">{cat.productCount}</span>
                    </button>
                  ))}
                </div>
              </div>
            </aside>

            {/* Products Section */}
            <div className="flex-1">
              {/* Toolbar */}
              <div className="bg-white rounded-2xl p-4 ring-1 ring-brand-divider shadow-sm mb-6 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowFilters(true)}
                    className="lg:hidden inline-flex items-center gap-2 px-3 py-2 bg-brand-cream text-brand-orange-dark rounded-lg text-sm font-medium"
                  >
                    <Filter className="w-4 h-4" />
                    Filter
                  </button>
                  <p className="text-sm text-brand-brown-light">
                    <span className="font-semibold text-brand-brown">{filteredProducts.length}</span> product{filteredProducts.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="appearance-none bg-white border border-brand-divider text-brand-brown text-sm rounded-lg px-4 py-2 pr-9 focus:outline-none focus:ring-2 focus:ring-brand-orange cursor-pointer"
                  >
                    <option value="featured">Featured</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="name">Name: A-Z</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-brown-light pointer-events-none" />
                </div>
              </div>

              {/* Product Grid */}
              {filteredProducts.length === 0 ? (
                <div className="bg-white rounded-2xl p-12 text-center ring-1 ring-brand-divider">
                  <p className="text-brand-brown-light">No products found in this category.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredProducts.map((product) => (
                    <div
                      key={product.id}
                      className="bg-white rounded-2xl shadow-sm ring-1 ring-brand-divider hover:shadow-xl hover:-translate-y-1 transition-all overflow-hidden flex flex-col group"
                    >
                      <Link to={`/product/${product.slug}`} className="block relative bg-brand-cream">
                        <img
                          className="w-full object-contain h-64 p-6 group-hover:scale-105 transition-transform duration-500"
                          src={product.image}
                          alt={product.name}
                        />
                        {product.badge && (
                          <span className="absolute top-3 left-3 bg-brand-orange text-white text-xs font-bold px-2.5 py-1 rounded-full">
                            {product.badge}
                          </span>
                        )}
                      </Link>
                      <div className="p-5 flex flex-col flex-grow justify-between">
                        <div>
                          <Link
                            to={`/product/${product.slug}`}
                            className="block text-base font-semibold text-brand-brown hover:text-brand-orange-dark line-clamp-2 min-h-[3rem]"
                          >
                            {product.name}
                          </Link>
                          <p className="text-sm text-brand-brown-light my-2 line-clamp-2">
                            {product.description.slice(0, 80)}...
                          </p>
                        </div>
                        <div>
                          <div className="text-brand-orange font-extrabold text-xl mb-3">
                            ${product.price.toFixed(2)}
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={(e) => handleQuickAdd(e, product)}
                              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 bg-brand-orange text-white text-sm font-semibold rounded-full hover:bg-brand-orange transition"
                            >
                              <ShoppingCart className="w-4 h-4" />
                              Add
                            </button>
                            <Link
                              to={`/product/${product.slug}`}
                              className="flex-1 inline-flex items-center justify-center px-3 py-2.5 border border-brand-divider text-brand-orange-dark text-sm font-semibold rounded-full hover:bg-brand-cream transition"
                            >
                              Learn More
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ShopPage;
